import { clsx } from 'clsx'
import { head } from 'ramda'
import { Badge, withStyles } from '@material-ui/core'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'

import { getHumanizeDate } from 'commons/date'
import { resizeAvatar_80 } from 'commons/resizeImage'
import { getFullPhoneNumber } from 'commons/phoneNumber'
import {
  subscriberStatus,
  SubscriberMenuAction,
  ACTION_SOURCE_ROW,
} from 'app/constants/subscriberCRM'

import Avatars from 'app/componentV4/Avatar/Avatars'
import Restricted from 'app/componentV4/Restricted'
import StatusChip from 'app/componentV4/Status/Chip'
import ChannelIcon from 'app/componentV4/Channel/ChannelIcon'
import TruncateWithTooltip from 'app/componentV4/Table/TruncateWithTooltip'
import SubscriberAction from 'app/main/subscribersV4/actions/SubscriberAction'

const StyledBadge = withStyles({
  badge: {
    width: '25px',
    padding: 0,
    alignContent: 'flex-end',
    justifyContent: 'flex-end',
  },
})(Badge)

export const ColumnOfData = (
  formatMessage,
  isDownXs,
  onAction,
  showZipCode = false,
) => {
  const columns = [
    {
      header: formatMessage({ id: 'firstName.label' }),
      accessor: 'firstName',
      style: {
        minWidth: '200px',
        maxWidth: '230px',
        left: isDownXs ? 0 : 62,
        position: isDownXs ? 'unset' : 'sticky',
      },
      sort: true,
      render: (row) => (
        <div className="flex space-x-3">
          <StyledBadge
            overlap="circle"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={
              <WarningRoundedIcon
                fontSize="small"
                style={{ color: '#D97706' }}
                className={clsx(!row.hasBadContact && 'hidden')}
              />
            }
          >
            <Avatars
              id={row.id}
              name={`${row.firstName} ${row.lastName}`}
              src={resizeAvatar_80(row.avatar)}
              characters={
                row.avatarCharacters ??
                `${head(row.firstName)}${head(row.lastName)}`
              }
              className={'w-32 h-32 text-14 font-bold'}
            />
          </StyledBadge>
          <TruncateWithTooltip
            value={row.firstName}
            className="my-auto truncate"
          />
        </div>
      ),
    },
    {
      header: formatMessage({ id: 'lastName.label' }),
      accessor: 'lastName',
      style: {
        minWidth: '200px',
        maxWidth: '230px',
      },
      sort: true,
      render: (row) => (
        <div className="flex">
          <TruncateWithTooltip
            value={row.lastName}
            className="my-auto truncate"
          />
        </div>
      ),
    },
    {
      header: formatMessage({ id: 'email.address.label' }),
      accessor: 'email',
      style: { minWidth: '180px', maxWidth: '230px', zIndex: 0 },
      sort: true,
      render: (row) => {
        return row.hideContacts === 1 ? (
          <Restricted />
        ) : (
          <TruncateWithTooltip value={row.email} className="truncate" />
        )
      },
    },
    {
      header: formatMessage({ id: 'phone.label' }),
      accessor: 'phone',
      style: {
        minWidth: '150px',
      },
      sort: true,
      render: (row) => {
        return row.hideContacts === 1 ? (
          <Restricted />
        ) : (
          getFullPhoneNumber(row.countryCode, row.phoneNumber)
        )
      },
    },
    {
      header: formatMessage({ id: 'channels.label' }),
      accessor: 'channel',
      style: {
        minWidth: '100px',
      },
      render: (row) => {
        return (
          <ChannelIcon
            channel={Number(row.channelSelected.channel)}
            isTooltip={true}
          />
        )
      },
    },
  ]

  // Conditionally add zip code column after channels
  if (showZipCode) {
    columns.push({
      header: formatMessage({ id: 'filter.quickfilter.zip.code' }),
      accessor: 'zipCode',
      style: {
        minWidth: '120px',
      },
      render: (row) => {
        // Try to get zip code from address object, fallback to empty string
        const zipCode =
          row?.address?.physicalPostalCode ||
          row?.address?.mailingPostalCode ||
          row?.physicalPostalCode ||
          row?.mailingPostalCode ||
          row?.zipCode ||
          ''
        return zipCode ? (
          <TruncateWithTooltip value={zipCode} className="truncate" />
        ) : (
          '--'
        )
      },
    })
  }

  // Add the rest of the columns
  columns.push(
    {
      header: formatMessage({ id: 'text.status' }),
      accessor: 'lastInvitationStatus',
      style: {
        minWidth: '150px',
      },
      render: ({ lastInvitationStatus }) => (
        <StatusChip
          type={subscriberStatus[lastInvitationStatus].type}
          label={formatMessage({
            id: subscriberStatus[lastInvitationStatus].label,
          })}
        />
      ),
    },
    {
      header: formatMessage({ id: 'preferred.language.label' }),
      accessor: 'name',
      style: {
        minWidth: '150px',
      },
      render: (row) => row.language.name,
    },
    {
      header: formatMessage({ id: 'lastActive.label' }),
      accessor: 'lastActiveDate',
      sort: true,
      style: {
        minWidth: '180px',
      },
      render: (row) => getHumanizeDate(row.lastActiveDate),
    },
    {
      header: formatMessage({ id: 'lastSeen.label' }),
      accessor: 'lastSeenDate',
      sort: true,
      style: {
        minWidth: '180px',
      },
      render: (row) => getHumanizeDate(row.lastSeenDate),
    },
    {
      header: formatMessage({ id: 'source.label' }),
      accessor: 'source',
      style: {
        minWidth: '180px',
      },
      render: (row) =>
        formatMessage({ id: row.source ? `${row.source}` : 'unknown.label' }),
    },
    {
      header: formatMessage({ id: 'addedDate.label' }),
      accessor: 'p.created_at',
      sort: true,
      style: {
        minWidth: '180px',
      },
      render: (row) => getHumanizeDate(row.createdAt),
    },
    {
      header: '',
      action: true,
      render: (row) => (
        <>
          <SubscriberAction
            subscriberId={row.id}
            channel={row?.channelSelected?.channel}
            status={row.lastInvitationStatus}
            hasBadContact={row?.hasBadContact}
            isHorizontal={false}
            handleInvite={() =>
              onAction(SubscriberMenuAction.INVITE, [row.id], ACTION_SOURCE_ROW)
            }
            handleReInvite={() =>
              onAction(
                SubscriberMenuAction.REINVITE,
                [row.id],
                ACTION_SOURCE_ROW,
              )
            }
            handleDelete={() =>
              onAction(SubscriberMenuAction.DELETE, [row.id], ACTION_SOURCE_ROW)
            }
            handleApprove={() =>
              onAction(
                SubscriberMenuAction.APPROVE,
                [row.id],
                ACTION_SOURCE_ROW,
              )
            }
            handleReject={() =>
              onAction(SubscriberMenuAction.REJECT, [row.id], ACTION_SOURCE_ROW)
            }
            handleMarkCorrect={() =>
              onAction(
                SubscriberMenuAction.MARK_CORRECT,
                row.id,
                ACTION_SOURCE_ROW,
              )
            }
          />
        </>
      ),
    },
  )

  return columns
}
